import React from "react";
import styled from "styled-components";
import { useAppSelector } from "../../store/hooks";
import { url } from "../../api";
import {
  useGetArtistPageQuery,
  useToggleStandalonePageMutation,
} from "../../services/rtkApi/artistsGalleryApi";

function roleHas(roleStr: string | null | undefined, target: string): boolean {
  if (!roleStr) return false;
  return roleStr
    .split(",")
    .map((r) => r.trim())
    .includes(target);
}

function standaloneUrl(artworkId: string): string {
  return `${url}/artworks/${artworkId}`;
}

const ArtistStandaloneSettings: React.FC = () => {
  const username = useAppSelector((s) => s.auth.username);
  const role = useAppSelector((s) => s.auth.role);
  const bootstrapStatus = useAppSelector((s) => s.auth.bootstrapStatus);
  const isAdmin = roleHas(role, "admin");
  const isContributor = roleHas(role, "contributor");
  const show =
    bootstrapStatus === "ready" &&
    !!username &&
    (isAdmin || isContributor);

  const { data, isLoading, error } = useGetArtistPageQuery(username!, {
    skip: !show || !username,
  });
  const [toggleStandalone, { isLoading: isToggling }] =
    useToggleStandalonePageMutation();

  if (!show) return null;

  const artworks = data?.artworks ?? [];

  return (
    <Section>
      <SectionTitle>Standalone archive pages</SectionTitle>
      <Blurb>
        Each piece has a permanent page for sharing and Wayback capture.{" "}
        <strong>Public link</strong> controls whether that page is indexed and
        treated as public; the URL still works when off. Only admins can turn
        the public link on or off.
      </Blurb>

      {isLoading && <Note>loading your gallery…</Note>}
      {error && <Warning>could not load artworks.</Warning>}
      {!isLoading && !error && artworks.length === 0 && (
        <Note>no artworks on your gallery yet.</Note>
      )}
      {!isLoading && !error && artworks.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>piece</Th>
              <Th>standalone page</Th>
              <Th>public link</Th>
              {isAdmin && <Th></Th>}
            </tr>
          </thead>
          <tbody>
            {artworks.map((a) => (
              <tr key={a._id}>
                <Td>{a.title}</Td>
                <Td>
                  <SmallLink
                    href={standaloneUrl(a._id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    open
                  </SmallLink>
                </Td>
                <Td>
                  <StatusBadge $on={a.standalonePageEnabled}>
                    {a.standalonePageEnabled ? "on" : "off"}
                  </StatusBadge>
                </Td>
                {isAdmin && (
                  <Td>
                    <ToggleBtn
                      type="button"
                      disabled={isToggling}
                      onClick={() =>
                        toggleStandalone({
                          id: a._id,
                          enabled: !a.standalonePageEnabled,
                        })
                      }
                    >
                      turn {a.standalonePageEnabled ? "off" : "on"}
                    </ToggleBtn>
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Section>
  );
};

const Section = styled.section`
  width: 100%;
  max-width: 760px;
  margin: 24px auto 28px;
  padding: 20px;
  background: #ffffff;
  border: 3px solid #222;
  box-shadow: 4px 6px rgba(0, 0, 0, 0.2);
`;

const SectionTitle = styled.h2`
  margin: 0 0 10px 0;
  font-size: 1.35rem;
  color: #111;
`;

const Blurb = styled.p`
  margin: 0 0 16px 0;
  font-size: 0.92rem;
  line-height: 1.45;
  color: #444;
`;

const Note = styled.div`
  font-size: 0.95rem;
  color: #333;
`;

const Warning = styled.div`
  background: #ffd1d1;
  border: 2px solid #000;
  padding: 10px 12px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  td,
  th {
    border-bottom: 1px solid #ccc;
    padding: 10px 8px;
    text-align: left;
    vertical-align: middle;
  }

  th {
    font-weight: 700;
    color: #111;
    border-bottom: 2px solid #000;
  }
`;

const Th = styled.th``;

const Td = styled.td``;

const SmallLink = styled.a`
  font-weight: 700;
  color: #085;
`;

const StatusBadge = styled.span<{ $on: boolean }>`
  display: inline-block;
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 3px 10px;
  border-radius: 999px;
  border: 2px solid #000;
  background: ${(p) => (p.$on ? "#d6f5e1" : "#eee")};
`;

const ToggleBtn = styled.button`
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 4px 10px;
  border: 2px solid #000;
  background: #fff77a;
  cursor: pointer;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.25);

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  &:hover:not(:disabled) {
    transform: translate(1px, 1px);
    box-shadow: 1px 2px rgba(0, 0, 0, 0.25);
  }
`;

export default ArtistStandaloneSettings;
